import * as React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import {
  ClickAwayListener,
  Typography,
  Popover,
  FormControl,
  InputAdornment,
  Input,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  ListItemButton,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { StyledFormControl } from "../../../../components/StyledComponents/Inputs/Inputs.js";
import styled from "@emotion/styled";
import {
  BodyLarge,
  BodySmall,
  H6HeadlineSmall,
  H7TitleLarge,
  H8TitleMedium,
} from "../../../../components/StyledComponents/Typography/Typography.tsx";
import { SecondaryButton } from "../../../../components/StyledComponents/Buttons/AuradineButtons";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { useGetSiteLists } from "../../../../api/queries/useGetSiteLists.ts";
import useMinerPageContext from "../../../context/useMinerPageContext.js";
import useOrgSiteGroupTreeContext from "../../../context/useOrgSiteGroupTreeContext.js";
import { useMoveDGMember } from "../../../queries/useMoveDGMember.ts";
import { useQueryClient } from "@tanstack/react-query";
import MinerConfigurationDialog from "../../ConfigurationModal/ConfigurationModal.js";
import { CustomTHSForm } from "../../CustomizationModal/CustomizationModal.js";
import { useTheme } from "../../../../ThemeContext/ThemeContext.js";

const StyledPopoverContent = styled(Box)`
  display: flex;
  flex-direction: column;
  background: #f3f2fd;
  border-radius: 1rem;

  box-shadow: 0px 2px 6px 2px rgba(0, 0, 0, 0.15),
    0px 1px 2px 0px rgba(0, 0, 0, 0.3);
`;

const StyledPopover = styled(Popover)`
  & .MuiPaper-root {
    border-radius: 1rem; // Change this to the border radius you want
  }
`;

const StyledHeaderAndFooterBox = styled(Box)`
  padding: 1.5rem;
`;

const GroupOperationsCard = (props) => {
  const { open, handleClose, anchorRef, refetch: minerPageRefetch, resetTableSelection, setTableKey } = props;
  const queryClient = useQueryClient();
  const { colors } = useTheme();
  const history = useHistory();

  const [siteAndGroups, setSiteAndGroups] =
    React.useState();

  //fetching to get userPreferences to set the tableGroup and detailGroup from user object. If not present, we are setting default values
  const [authToken, setAuthToken] = React.useState("");
  const { orgSelectedName, siteSelectedName, groupSelectedName } = useOrgSiteGroupTreeContext();
  const { selectedSerials, groupFilters, setSelectedSerials } = useMinerPageContext();
  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();

  React.useEffect(() => {
    console.log(1);
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  const {
    data: siteLists,
    isLoading: isSiteGroupDataLoading,
    isFetching: isSiteGroupDataFetching,
    isFetched: isSiteGroupDataFetched,
    refetch: siteListRefetch,
  } = useGetSiteLists({
    authToken,
    leftPanelOpen: open,
  });

  const { mutate: moveMiners } = useMoveDGMember();

  React.useEffect(() => {
    if (siteLists) {
      if (
        siteLists?.status === "ok" &&
        siteLists?.siteList?.length > 0
      ) {
        const siteList = siteLists?.siteList;
        setSiteAndGroups(siteList);
      }
    }
  }, [siteLists, isSiteGroupDataFetching]);

  const onSitesAndGroupSearch = (e) => {
    if (e.target.value === "") {
      setSiteAndGroups(siteLists?.siteList);
      return;
    } else {
      const sitesData = [...siteAndGroups];
      const foundSite = sitesData.filter((site) => {
        return (
          site?.siteName
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          (site.groups &&
            site.groups.some((group) =>
              group.DGName.toLowerCase().includes(
                e.target.value.toLowerCase()
              )
            ))
        );
      });

      setSiteAndGroups(foundSite);
    }
  };
  const [searchValue, setSearchValue] = React.useState("");

  return (
    <StyledPopover
      sx={{ zIndex: 9999, borderRadius: "1rem" }}
      open={open}
      anchorEl={anchorRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      colors={colors}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <StyledPopoverContent colors={colors}>
          <StyledHeaderAndFooterBox
            display={"flex"}
            flexDirection={"column"}
            gap={4}
          >
            <H7TitleLarge>Move to:</H7TitleLarge>
            <BodySmall>
              Select which group you would like to transfer the
              miners to.
            </BodySmall>
          </StyledHeaderAndFooterBox>
          <Box
            display="flex"
            flexDirection={"column"}
            padding="0rem 1.5rem"
            className={"search-box"}
          >
            {/* Search Input */}
            <StyledFormControl colors={colors}
              fullWidth
              sx={{ m: 1 }}
              variant="standard"
            >
              <Input
                id="standard-adornment-amount"
                placeholder="Search groups"
                onChange={(event) =>
                  onSitesAndGroupSearch(event)
                }
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <CloseIcon sx={{ color: colors?.text?.muted }} />
                  </InputAdornment>
                }
              />
            </StyledFormControl>

            {/* Groups List */}
            <Box
              display={"flex"}
              flexDirection={"column"}
              width={"22.5rem"}
              minWidth={"22.5rem"}
              maxHeight={"15rem"}
              minHeight={"15rem"}
              maxWidth={"45rem"}
              overflow={"auto"}
            >
              {siteAndGroups &&
                Array.isArray(siteAndGroups) &&
                siteAndGroups.length > 0 &&
                siteAndGroups.map((site, siteIndex) => (
                  <React.Fragment key={siteIndex}>
                    <H8TitleMedium key={siteIndex}>
                      {site.siteName}
                    </H8TitleMedium>

                    {site.groups &&
                      site.groups.length > 0 &&
                      site.groups.map(
                        (group, groupIndex) => (
                          <React.Fragment
                            key={groupIndex}>
                            <ListItemButton
                              key={groupIndex}
                              disabled={groupSelectedName === group.DGName}
                              onClick={async () => {
                                moveMiners({
                                  serials: selectedSerials,
                                  sourceSite: siteSelectedName,
                                  sourceGroup: groupSelectedName,
                                  destinationSite: site.siteName,
                                  destinationgroup: group.DGName,
                                  filter: groupFilters
                                },
                                  {
                                    onSuccess: async (data) => {
                                      await new Promise((r) => setTimeout(r, 300));
                                      console.log("Move miners success");
                                      console.log("miner page refetch", minerPageRefetch);
                                      await minerPageRefetch().then((data) => {
                                        console.log("Miner page refetched", data);
                                      });
                                      await queryClient.invalidateQueries({ queryKey: ["getSiteLists", authToken] });
                                      await siteListRefetch().then((data) => {
                                        console.log("Site list refetched", data);
                                      });
                                      resetTableSelection();
                                      setTableKey((prev) => prev + 1);
                                      handleClose();
                                    },
                                    onError: (error) => {
                                      console.log("Move miners error", error);
                                      handleClose();
                                    },
                                  }
                                )
                                // resetTableSelection();


                              }}
                              sx={{
                                minHeight: "3rem",
                                maxHeight: "3rem",
                                "&:hover": {
                                  backgroundColor:
                                    colors.hover.muted,
                                  borderRadius: "6.5rem",
                                  cursor: "pointer",
                                },
                              }}
                            >
                              {group.DGName}
                            </ListItemButton>
                          </React.Fragment>
                        )
                      )}
                  </React.Fragment>
                ))}
            </Box>
          </Box>
          <Box>
            <List
              sx={{
                padding: "0.25rem 1.5rem",
                borderTop: "2px solid #CAC4D0",
              }}
            >
              <ListItem
                sx={{
                  display: "flex",
                  height: "3rem",
                  minHeight: "3rem",
                }}
              >
                <ListItemButton
                  sx={{
                    display: "flex",
                    height: "3rem",
                    minHeight: "3rem",
                    "&:hover": {
                      backgroundColor: colors.hover.muted,
                    },
                  }}
                  onClick={() => {
                    handleClose();
                    history.push("/sitegroupmanagement");
                  }}
                >
                  <BodyLarge> Manage groups</BodyLarge>
                </ListItemButton>
                <ArrowRightIcon />
              </ListItem>
            </List>
          </Box>
        </StyledPopoverContent>
      </ClickAwayListener>
    </StyledPopover>
  );
};

export default GroupOperationsCard;

export const GroupOperationsTab = (props) => {
  const { refetch: minerPageRefetch, resetTableSelection, setTableKey } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { selectedSerials } = useMinerPageContext();

  const { orgSelectedName, siteSelectedName, groupSelectedName } = useOrgSiteGroupTreeContext();
  const anchorRef = React.useRef(null);
  const { user } = useAuth0();
  const [open, setOpen] = React.useState(false);
  const navigate = useHistory();
  const handleClick = (event) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };
  const siteOrGroupSelected =
    siteSelectedName !== "" || (siteSelectedName !== "" && groupSelectedName !== "");

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <React.Fragment>
      <GroupOperationsCard
        open={open}
        handleClose={handleClose}
        anchorRef={anchorRef}
        refetch={minerPageRefetch}
        resetTableSelection={resetTableSelection}
        setTableKey={setTableKey}
      />
    </React.Fragment>
  );
};
